import * as React from "react";
import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { FormControl, Input, InputWrapper } from "kanopi-components/Input";
import { Notification } from "baseui/notification";
import { StripeElementStyleVariant } from "@stripe/stripe-js";
import { usePayment } from "kw/quoting/components/PaymentForm/usePayment";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeError } from "@stripe/stripe-js";

/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-types */

// TODO: Enable the proptype check next time when this file is changed
function addTestId(id) {
  return {
    Block: {
      props: {
        "data-testid": id,
      },
    },
  };
}

export type TUpdatePaymentMethodFormProps = {
  onSubmit?: () => void;
};

// TODO: This form is duplicated from the StripeForm component.
// We should refactor the StripeForm to be more generic and reusable.
const StripePaymentMethodForm: React.FC<TUpdatePaymentMethodFormProps> = ({
  onSubmit,
}) => {
  const [name, setName] = React.useState("");
  const stripe = useStripe();
  const elements = useElements();
  const [stripeLoading, setStripeLoading] = React.useState(false);
  const [stripeError, setStripeError] = React.useState<StripeError>(null);
  const stripeReady = !!stripe;
  // change to payment method form submit
  const { submitPaymentMethod: paymentMethodSubmit, loading: paymentLoading } =
    usePayment({ onSubmit: onSubmit });

  const disableSubmit = !stripeReady || stripeLoading || paymentLoading;
  const elementOptions = useCustomElementOptions();

  const stripeSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    try {
      setStripeLoading(true);
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: { name },
      });
      setStripeError(error);
      if (!error) {
        paymentMethodSubmit({
          payment_method_id: paymentMethod.id,
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <form onSubmit={stripeSubmit}>
      {/*
        Forcing 'focus' for easy solution to mismatching
        styles between our app and stripe elements
      */}
      <InputWrapper>
        <FormControl label="Name on card" forceFocus>
          {/* TODO: Validations on this? */}
          <Input
            required
            id="name_on_card"
            name="name_on_card"
            onChange={(e) => setName(e.currentTarget.value)}
            value={name}
          />
        </FormControl>
      </InputWrapper>
      <InputWrapper overrides={addTestId("card-number-box")}>
        <FormControl label="Card number" forceFocus>
          <CardNumberElement options={elementOptions} />
        </FormControl>
      </InputWrapper>
      <InputWrapper>
        <Block display="flex">
          <Block
            width="65%"
            marginRight="scale700"
            overrides={addTestId("card-expiry-box")}
          >
            <FormControl label="Expiry" forceFocus>
              <CardExpiryElement options={elementOptions} />
            </FormControl>
          </Block>
          <Block flex={1} overrides={addTestId("card-cvc-box")}>
            <FormControl label="CVC" forceFocus>
              <CardCvcElement options={elementOptions} />
            </FormControl>
          </Block>
        </Block>
      </InputWrapper>
      {stripeError && (
        <Notification
          kind="negative"
          overrides={{ Body: { style: { width: "auto" } } }}
        >
          {stripeError.type === "card_error"
            ? stripeError.message
            : "There was an error sending your payment request, please try again."}
        </Notification>
      )}
      <Block display="flex" justifyContent="center" marginTop="scale1200">
        <Button
          type="submit"
          kind="primary"
          disabled={disableSubmit}
          isLoading={stripeLoading || paymentLoading}
          wide
          // TODO: Rebuild Button
          overrides={{
            BaseButton: {
              style: ({ $theme }) => ({
                [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                  width: "100%",
                },
              }),
            },
          }}
        >
          Update payment method
        </Button>
      </Block>
    </form>
  );
};

const useCustomElementOptions = () => {
  const [css, theme] = useStyletron();

  const inputWrapperClassNames = css({
    paddingBottom: "10px",
    borderBottom: `1px solid ${theme.colors.inputBorder}`,
    ...theme.typography.ParagraphMedium,
  });

  const baseInputStyles: StripeElementStyleVariant = {
    fontFamily: theme.typography.ParagraphMedium.fontFamily,
    fontSize: theme.typography.ParagraphMedium.fontSize,
    fontWeight: `${theme.typography.ParagraphMedium.fontWeight}`,
  };

  const elementOptions = {
    style: { base: baseInputStyles },
    classes: { base: inputWrapperClassNames },
  };

  return elementOptions;
};

export { StripePaymentMethodForm };
